import { applyAction, resolveHand } from './betting';
import { burn, createDeck, deal } from './deck';
import type { HandEvaluator, Rng } from './ports';
import {
  actingPlayers,
  emptyBettingState,
  firstToActSeatHeadsUp,
  isHandOver,
  nextSeatClockwise,
  startedHeadsUp,
} from './rules';
import type {
  Action,
  BlindLevel,
  Card,
  Event,
  HandState,
  Player,
  PlayerId,
  Seat,
  Street,
} from './types';

export type ReducerServices = {
  rng: Rng;
  evaluator: HandEvaluator;
};

export type ReducerResult = {
  state: HandState;
  events: Event[];
};

/**
 * Street metadata in one table so `next`, `dealt`, and `total` can't drift
 * apart. `dealt` = cards turned on entry to this street; `total` = board
 * size once the street is live.
 */
const STREET_META: Record<Street, { next: Street; dealt: number; total: number }> = {
  preflop: { next: 'flop', dealt: 0, total: 0 },
  flop: { next: 'turn', dealt: 3, total: 3 },
  turn: { next: 'river', dealt: 1, total: 4 },
  river: { next: 'showdown', dealt: 1, total: 5 },
  showdown: { next: 'payout', dealt: 0, total: 5 },
  payout: { next: 'payout', dealt: 0, total: 5 },
};

function sortedSeats(players: ReadonlyArray<Player>): Seat[] {
  return players.map((p) => p.seat).sort((a, b) => a - b);
}

function postAnte(player: Player, amount: number): Player {
  if (amount <= 0) return player;
  const posted = Math.min(amount, player.stack);
  return {
    ...player,
    stack: player.stack - posted,
    handContribution: player.handContribution + posted,
    allIn: player.stack - posted === 0 && !player.folded,
  };
}

function postBlind(player: Player, amount: number): Player {
  if (amount <= 0) return player;
  const posted = Math.min(amount, player.stack);
  return {
    ...player,
    stack: player.stack - posted,
    streetContribution: player.streetContribution + posted,
    handContribution: player.handContribution + posted,
    allIn: player.stack - posted === 0 && !player.folded,
  };
}

/**
 * Start a new hand: shuffle deck, post blinds (+ BB-only ante from L5),
 * deal hole cards, set the first pendingActor.
 *
 * Expects `players` already pre-filtered for the hand (eliminated players
 * are allowed to be present but will be skipped). Hand-scoped fields
 * (folded/allIn/handContribution/streetContribution/holeCards) should be reset
 * by the caller prior to this call.
 */
export function startHand(input: {
  handNumber: number;
  level: BlindLevel;
  seed: string;
  button: Seat;
  players: ReadonlyArray<Player>;
  rng: Rng;
}): ReducerResult {
  const { handNumber, level, seed, button, players, rng } = input;
  const events: Event[] = [{ type: 'handStart', handNumber, level, seed }];

  const eligible = players.filter((p) => !p.eliminated && p.stack > 0);
  if (eligible.length < 2) {
    throw new Error(`startHand requires at least 2 eligible players, got ${eligible.length}`);
  }
  const eligibleSeats = sortedSeats(eligible);
  const headsUp = eligibleSeats.length === 2;

  const sbSeat: Seat = headsUp ? button : nextSeatClockwise(button, eligibleSeats);
  const bbSeat: Seat = nextSeatClockwise(sbSeat, eligibleSeats);

  const shuffled = rng.shuffle(createDeck());
  let deck: Card[] = shuffled;

  let updatedPlayers: Player[] = players.map((p) => ({ ...p }));

  // Ante first (BB only, from L5+ where level.ante > 0)
  if (level.ante > 0) {
    updatedPlayers = updatedPlayers.map((p) =>
      p.seat === bbSeat && !p.eliminated && p.stack > 0 ? postAnte(p, level.ante) : p,
    );
  }

  // Post blinds
  updatedPlayers = updatedPlayers.map((p) => {
    if (p.eliminated || p.stack === 0) return p;
    if (p.seat === sbSeat) return postBlind(p, level.sb);
    if (p.seat === bbSeat) return postBlind(p, level.bb);
    return p;
  });

  // Deal hole cards starting at SB, 2 per player
  const dealOrder: Seat[] = [];
  let cursor = sbSeat;
  for (let i = 0; i < eligibleSeats.length; i++) {
    dealOrder.push(cursor);
    cursor = nextSeatClockwise(cursor, eligibleSeats);
  }
  for (let round = 0; round < 2; round++) {
    for (const seat of dealOrder) {
      const idx = updatedPlayers.findIndex((p) => p.seat === seat);
      if (idx < 0) continue;
      const player = updatedPlayers[idx] as Player;
      if (player.eliminated) continue;
      const { dealt, rest } = deal(deck, 1);
      deck = rest;
      const existing = player.holeCards ?? (['__', '__'] as unknown as readonly [Card, Card]);
      const nextHole: readonly [Card, Card] =
        round === 0
          ? ([dealt[0] as Card, existing[1]] as const)
          : ([existing[0], dealt[0] as Card] as const);
      updatedPlayers[idx] = { ...player, holeCards: nextHole };
    }
  }

  for (const p of updatedPlayers) {
    if (p.holeCards && !p.eliminated) {
      events.push({ type: 'deal', playerId: p.id, cards: p.holeCards });
    }
  }

  const betting = {
    ...emptyBettingState(level, 'preflop'),
    currentBet: level.bb,
    lastRaiseSize: level.bb,
    minRaise: level.bb,
  };

  const activeForAction = updatedPlayers.filter((p) => !p.eliminated && !p.allIn && p.stack > 0);
  const actionSeats = sortedSeats(activeForAction);
  let firstActorSeat: Seat | null = null;
  if (actionSeats.length > 0) {
    if (headsUp) {
      firstActorSeat = button; // SB/button acts first preflop heads-up
      if (!actionSeats.includes(firstActorSeat)) {
        firstActorSeat = nextSeatClockwise(firstActorSeat, actionSeats);
      }
    } else {
      firstActorSeat = nextSeatClockwise(bbSeat, actionSeats);
    }
  }
  const firstActor =
    firstActorSeat !== null ? updatedPlayers.find((p) => p.seat === firstActorSeat) : undefined;

  const state: HandState = {
    handNumber,
    level,
    seed,
    button,
    board: [],
    deck,
    players: updatedPlayers,
    betting,
    actionHistory: [],
    pendingActor: firstActor?.id ?? null,
  };

  return { state, events };
}

function isAggressive(state: HandState, action: Action, before: HandState): boolean {
  if (action.type === 'bet' || action.type === 'raise') return true;
  if (action.type === 'allin') {
    return state.betting.currentBet > before.betting.currentBet;
  }
  return false;
}

function updateActedInRound(before: HandState, after: HandState, action: Action): HandState {
  let acted = [...after.betting.actedInRound];
  if (isAggressive(after, action, before)) {
    acted = [action.playerId];
  } else if (!acted.includes(action.playerId)) {
    acted = [...acted, action.playerId];
  }
  return { ...after, betting: { ...after.betting, actedInRound: acted } };
}

function isStreetComplete(state: HandState): boolean {
  const acting = actingPlayers(state);
  if (acting.length === 0) return true;
  const allMatched = acting.every((p) => p.streetContribution === state.betting.currentBet);
  const allActed = acting.every((p) => state.betting.actedInRound.includes(p.id));
  return allMatched && allActed;
}

function computeNextActor(state: HandState): PlayerId | null {
  const acting = actingPlayers(state);
  if (acting.length === 0) return null;
  const actingSeats = sortedSeats(acting);
  const current = state.players.find((p) => p.id === state.pendingActor);
  const baseSeat: Seat = current ? current.seat : state.button;
  const nextSeat = nextSeatClockwise(baseSeat, actingSeats);
  return acting.find((p) => p.seat === nextSeat)?.id ?? null;
}

function revealRemainingBoard(state: HandState, events: Event[]): HandState {
  let board = [...state.board];
  let deck = [...state.deck];
  const streets: Street[] = ['flop', 'turn', 'river'];
  for (const street of streets) {
    const needed = STREET_META[street].total - board.length;
    if (needed > 0) {
      const { rest: afterBurn } = burn(deck);
      const { dealt, rest } = deal(afterBurn, needed);
      board = [...board, ...dealt];
      deck = rest;
      events.push({ type: 'boardReveal', street, cards: dealt });
    }
  }
  return { ...state, board, deck };
}

function resolveAndEmit(
  state: HandState,
  evaluator: HandEvaluator,
  events: Event[],
): ReducerResult {
  const nonFolded = state.players.filter((p) => !p.folded);
  let prepared = state;
  if (nonFolded.length > 1) {
    prepared = revealRemainingBoard(prepared, events);
  }
  const { state: resolved, results } = resolveHand(prepared, evaluator, prepared.board);
  events.push({ type: 'showdown', payouts: results.payouts });
  for (const payout of results.payouts) {
    events.push({
      type: 'potAward',
      playerId: payout.playerId,
      potIndex: payout.potIndex,
      amount: payout.amount,
    });
  }
  events.push({ type: 'handEnd', handNumber: resolved.handNumber });
  return { state: resolved, events };
}

function advanceStreet(
  state: HandState,
  services: ReducerServices,
  events: Event[],
): ReducerResult {
  const current = state.betting.street;
  const next = STREET_META[current].next;

  if (current === 'river' || next === 'showdown' || next === 'payout') {
    return resolveAndEmit(state, services.evaluator, events);
  }

  const { rest: afterBurn } = burn(state.deck);
  const { dealt, rest: afterDeal } = deal(afterBurn, STREET_META[next].dealt);
  const newBoard = [...state.board, ...dealt];
  events.push({ type: 'streetAdvance', street: next });
  events.push({ type: 'boardReveal', street: next, cards: dealt });

  const resetPlayers = state.players.map((p) => ({ ...p, streetContribution: 0 }));
  const newBetting = emptyBettingState(state.level, next);

  const stillActing = resetPlayers.filter(
    (p) => !p.folded && !p.eliminated && !p.allIn && p.stack > 0,
  );
  const actionSeats = sortedSeats(stillActing);
  const advanced: HandState = {
    ...state,
    players: resetPlayers,
    board: newBoard,
    deck: afterDeal,
    betting: newBetting,
    pendingActor: null,
  };

  if (actionSeats.length <= 1) {
    return resolveAndEmit(advanced, services.evaluator, events);
  }

  let firstActorSeat: Seat | null;
  if (startedHeadsUp(advanced)) {
    firstActorSeat = firstToActSeatHeadsUp(advanced, next);
    if (!actionSeats.includes(firstActorSeat)) {
      firstActorSeat = nextSeatClockwise(firstActorSeat, actionSeats);
    }
  } else {
    firstActorSeat = nextSeatClockwise(state.button, actionSeats);
  }
  const firstActor = stillActing.find((p) => p.seat === firstActorSeat);

  return {
    state: { ...advanced, pendingActor: firstActor?.id ?? null },
    events,
  };
}

/**
 * Process a single player action. Composes betting.applyAction with the
 * street/hand state machine:
 *   apply -> actedInRound update -> fold-out check -> street completion
 *   check -> next actor OR street advance OR showdown.
 *
 * Always returns an events[] alongside the next state so downstream modules
 * (M4 HH serializer, M5 UI animations) can subscribe without diffing snapshots.
 */
export function step(state: HandState, action: Action, services: ReducerServices): ReducerResult {
  const events: Event[] = [];
  const afterAction = applyAction(state, action);
  events.push({ type: 'actionTaken', action });

  const withActed = updateActedInRound(state, afterAction, action);

  if (isHandOver(withActed)) {
    return resolveAndEmit(withActed, services.evaluator, events);
  }

  if (isStreetComplete(withActed)) {
    return advanceStreet(withActed, services, events);
  }

  const nextActor = computeNextActor(withActed);
  return {
    state: { ...withActed, pendingActor: nextActor },
    events,
  };
}
